import React, { PropsWithChildren, ReactNode, useState } from 'react';
import { classNames } from '../../utils';
import { twMerge } from 'tailwind-merge';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ReactComponent as TooltipIcon } from '../icons/tooltip.svg';

const tooltipStyles =
  'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade text-violet11  select-none rounded-[4px] bg-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]';

export const LabelTooltip: React.FC<{
  children: string | ReactNode;
  tooltipClassName: string | undefined;
  iconClassName?: string | undefined;
  underlinedText?: string | undefined;
  underLinedTextClassName?: string | undefined;
  wrapperClassName?: string | undefined;
}> = ({
  children,
  tooltipClassName,
  iconClassName,
  underlinedText,
  underLinedTextClassName,
  wrapperClassName,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip.Provider delayDuration={100}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger asChild>
          <span
            className={classNames('cursor-pointer', wrapperClassName)}
            onClick={() => setOpen(true)}
          >
            {underlinedText ? (
              <div
                className={classNames(
                  'w-fit underline decoration-dotted decoration-sidewalk-300 underline-offset-4',
                  underLinedTextClassName
                )}
              >
                {underlinedText}
              </div>
            ) : (
              <TooltipIcon
                className={classNames(
                  'ml-1 mt-[-1px] h-[10px] w-[10px] text-sidewalk-400',
                  iconClassName
                )}
              />
            )}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={classNames(tooltipStyles, tooltipClassName)}
            sideOffset={5}
            style={{ zIndex: 9999 }}
          >
            <Tooltip.Arrow className="fill-white" />
            <div className="text-body-small">{children}</div>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export const InputRow: React.FC<
  PropsWithChildren<{
    label: ReactNode;
    name?: string;
    className?: string;
    labelClassName?: string;
    tooltipText?: string;
    tooltipClassName?: string;
    centerLabel?: boolean;
  }>
> = ({
  name,
  label,
  children,
  className,
  labelClassName,
  tooltipText,
  tooltipClassName,
  centerLabel,
}) => {
  return (
    <div className={twMerge('w-full', className)}>
      <div
        className={classNames(
          'flex items-center',
          centerLabel ? 'self-center' : 'self-auto'
        )}
      >
        <label
          htmlFor={name}
          className={twMerge(
            'typo-body-small typo-base block font-sans text-[0.875rem] leading-[1.225rem] tracking-[0.015625rem] text-sidewalk-900 ',
            labelClassName
          )}
        >
          {label}
        </label>
        {tooltipText ? (
          <LabelTooltip tooltipClassName={tooltipClassName}>
            {tooltipText}
          </LabelTooltip>
        ) : null}
      </div>

      <div className="mt-2 relative">{children}</div>
    </div>
  );
};
